import { useState, useRef, useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MessageSquare,
  Send,
  Bot,
  User,
  Loader2,
  Sparkles,
  X,
  Maximize2,
  Minimize2,
  RotateCcw,
} from 'lucide-react';
import { useNLQQuery } from '../../../api/mutations/dashboard.mutations';
import { useNLQSuggestions } from '../../../api/queries/dashboard.queries';
import { useAuthStore } from '../../../stores/auth.store';
import { ParsedNLQQuery } from '../../../api/queries/dashboard.queries';
import { SmartChart, AnalyticsResponse } from './visualizations';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  data?: NLQResponseData;
  timestamp: Date;
}

interface NLQResponseData {
  type: 'metric' | 'list' | 'chart' | 'table' | 'insight';
  value?: number;
  unit?: string;
  trend?: 'up' | 'down' | 'stable';
  items?: any[];
  chartData?: any[] | Record<string, any>; // Can be array or comparison object
  insights?: string[];
  suggestions?: string[];
  parsed?: ParsedNLQQuery;
  metadata?: {
    executionTimeMs: number;
    resultCount: number;
    toolName?: string;
    visualization?: 'kpi' | 'line' | 'bar' | 'pie' | 'table' | 'comparison' | 'comparison_bar';
  };
}

/**
 * Transform backend NLQ response to UI-friendly format
 */
function transformNLQResponse(
  result: unknown,
  parsed: ParsedNLQQuery,
  metadata: { executionTimeMs: number; resultCount: number; toolName?: string; visualization?: string }
): NLQResponseData {
  const { intent, dataSource } = parsed;
  const vizHint = metadata.visualization;

  // NEW: Use visualization hint from tool-based approach if available
  // This is more reliable than inferring from data structure
  if (vizHint && result) {
    // Handle pie chart data (categories, distributions)
    if (vizHint === 'pie' && Array.isArray(result)) {
      return {
        type: 'chart',
        chartData: result,
        insights: [`Showing ${result.length} categories`],
      };
    }

    // Handle line chart data (trends, time series)
    if (vizHint === 'line' && Array.isArray(result)) {
      return {
        type: 'chart',
        chartData: result.map((item: any) => ({
          date: item._id || item.date,
          revenue: item.revenue || 0,
          orders: item.orders || 0,
          cost: item.cost || 0,
          newMembers: item.newMembers || 0,
        })),
        insights: [`Showing ${result.length} data points`],
      };
    }

    // Handle bar chart data (rankings, comparisons)
    if (vizHint === 'bar' && Array.isArray(result)) {
      return {
        type: 'list',
        items: result.map((item: any) => ({
          name: typeof item.name === 'object' ? (item.name?.en || 'Unknown') : (item.name || 'Unknown'),
          revenue: item.revenue || 0,
          quantity: item.quantity || 0,
          usageCount: item.usageCount || 0,
          totalDiscount: item.totalDiscount || 0,
        })),
        chartData: result,
        insights: [`Showing ${result.length} items`],
      };
    }

    // Handle KPI data (single metrics)
    if (vizHint === 'kpi' && typeof result === 'object' && result !== null) {
      const r = result as Record<string, any>;
      if ('totalRevenue' in r) {
        return {
          type: 'metric',
          value: r.totalRevenue,
          unit: 'THB',
          insights: [
            `Total orders: ${r.totalOrders?.toLocaleString() || 0}`,
            `Average order value: ฿${r.avgOrderValue?.toFixed(2) || 0}`,
            r.totalDiscounts ? `Total discounts: ฿${r.totalDiscounts?.toLocaleString()}` : '',
          ].filter(Boolean),
        };
      }
      return {
        type: 'metric',
        value: r.totalMembers || r.prTotal || r.poTotal || r.count || 0,
        insights: Object.entries(r)
          .filter(([k, v]) => typeof v === 'number' && k !== '_id')
          .map(([k, v]) => `${k}: ${typeof v === 'number' ? v.toLocaleString() : v}`),
      };
    }

    // Handle table data (lists, inventories)
    if (vizHint === 'table') {
      const items = Array.isArray(result) ? result :
        (result as any)?.lowStockItems || (result as any)?.expiringItems || [];
      return {
        type: 'list',
        items: items.map((item: any) => ({
          name: typeof item.name === 'object' ? (item.name?.en || item.name?.th || 'Unknown') : (item.name || 'Unknown'),
          quantity: item.quantity || 0,
          unit: item.unit || '',
          minStockLevel: item.minStockLevel,
          expiryDate: item.expiryDate,
          daysUntilExpiry: item.daysUntilExpiry,
          status: item.daysUntilExpiry !== undefined ?
            (item.daysUntilExpiry <= 1 ? 'critical' : item.daysUntilExpiry <= 3 ? 'warning' : 'ok') :
            (item.quantity <= 0 ? 'critical' : item.quantity <= (item.minStockLevel || 0) ? 'low' : 'ok'),
        })),
        insights: [(result as any)?.count ? `${(result as any).count} items need attention` : `Showing ${items.length} items`],
      };
    }

    // Handle comparison data
    if (vizHint === 'comparison' && typeof result === 'object' && result !== null && 'current' in result) {
      const comparison = result as {
        current: { period: string; data: any };
        previous: { period: string; data: any };
        changes: Record<string, { value: number; change: number; direction: 'up' | 'down' | 'stable' }>;
      };

      const insights: string[] = [];
      const changes = comparison.changes || {};

      if (changes.totalRevenue) {
        const dir = changes.totalRevenue.direction;
        insights.push(`Revenue ${dir === 'up' ? 'increased' : dir === 'down' ? 'decreased' : 'stayed stable'} by ${Math.abs(changes.totalRevenue.change).toFixed(1)}% (฿${changes.totalRevenue.value.toLocaleString()})`);
      }
      if (changes.totalOrders) {
        insights.push(`Orders ${changes.totalOrders.direction === 'down' ? 'decreased' : 'increased'} by ${Math.abs(changes.totalOrders.change).toFixed(1)}% (${changes.totalOrders.value.toLocaleString()} orders)`);
      }
      if (changes.avgOrderValue) {
        insights.push(`Avg order value ${changes.avgOrderValue.direction === 'up' ? 'increased' : 'decreased'} by ${Math.abs(changes.avgOrderValue.change).toFixed(1)}% (฿${changes.avgOrderValue.value.toLocaleString()})`);
      }

      return {
        type: 'chart',
        chartData: result,
        items: [
          { period: comparison.current.period, ...comparison.current.data },
          { period: comparison.previous.period, ...comparison.previous.data },
        ],
        insights,
        trend: changes.totalRevenue?.direction || 'stable',
      };
    }

    // Handle comparison bar chart data (grouped bars)
    if (vizHint === 'comparison_bar' && Array.isArray(result)) {
      const barData = result as { name: string; current: number; previous: number; change: number; direction: string }[];

      const insights: string[] = [];
      barData.forEach((item) => {
        const dir = item.direction === 'up' ? 'increased' : item.direction === 'down' ? 'decreased' : 'stayed stable';
        insights.push(`${item.name} ${dir} by ${Math.abs(item.change).toFixed(1)}%`);
      });

      return {
        type: 'chart',
        chartData: result,
        insights,
        metadata: {
          ...metadata,
          visualization: 'comparison_bar',
        },
      };
    }
  }

  // Handle comparison data (from compare queries)
  if (intent === 'compare' && typeof result === 'object' && result !== null && 'current' in result && 'previous' in result) {
    const comparison = result as {
      current: { period: string; data: any };
      previous: { period: string; data: any };
      changes: Record<string, { value: number; change: number; direction: 'up' | 'down' | 'stable' }>;
    };

    const insights: string[] = [];
    const changes = comparison.changes;

    // Generate insights from changes with actual values
    if (changes.totalRevenue) {
      const direction = changes.totalRevenue.direction === 'up' ? 'increased' : changes.totalRevenue.direction === 'down' ? 'decreased' : 'stayed the same';
      const currentVal = comparison.current.data?.totalRevenue || 0;
      const prevVal = comparison.previous.data?.totalRevenue || 0;
      insights.push(`Total revenue this week (฿${currentVal.toLocaleString()}) is ${Math.abs(changes.totalRevenue.change)}% ${direction === 'decreased' ? 'lower' : 'higher'} than last week (฿${prevVal.toLocaleString()}).`);
    }
    if (changes.totalOrders) {
      const direction = changes.totalOrders.direction === 'up' ? 'increased' : changes.totalOrders.direction === 'down' ? 'decreased' : 'stayed the same';
      const currentVal = comparison.current.data?.totalOrders || 0;
      const prevVal = comparison.previous.data?.totalOrders || 0;
      insights.push(`Total orders this week (${currentVal}) are ${Math.abs(changes.totalOrders.change)}% ${direction === 'decreased' ? 'lower' : 'higher'} than last week (${prevVal}).`);
    }
    if (changes.avgOrderValue) {
      const currentVal = comparison.current.data?.avgOrderValue || 0;
      const prevVal = comparison.previous.data?.avgOrderValue || 0;
      insights.push(`Average order value (฿${currentVal.toFixed(2)}) is ${Math.abs(changes.avgOrderValue.change)}% ${changes.avgOrderValue.direction === 'down' ? 'lower' : 'higher'} than last week (฿${prevVal.toFixed(2)}).`);
    }

    // Return as chart type so it renders in SmartChart with comparison visualization
    return {
      type: 'chart',
      chartData: result, // Pass the full comparison object for SmartChart to handle
      items: [
        { period: comparison.current.period, ...comparison.current.data },
        { period: comparison.previous.period, ...comparison.previous.data },
      ],
      insights,
      trend: changes.totalRevenue?.direction || 'stable',
    };
  }

  // Handle count intent
  if (intent === 'count' && typeof result === 'object' && result !== null && 'count' in result) {
    const r = result as { count: number };
    return {
      type: 'metric',
      value: r.count,
      insights: [`Found ${r.count} ${dataSource} records matching your query.`],
    };
  }

  // Handle sales/orders data
  if (dataSource === 'sales' || dataSource === 'orders') {
    // Trend data (array with time series)
    if (intent === 'trend' && Array.isArray(result)) {
      return {
        type: 'chart',
        chartData: result.map((item: any) => ({
          date: item._id,
          revenue: item.revenue || 0,
          orders: item.orders || 0,
        })),
        insights: [
          `Showing ${result.length} data points`,
          result.length > 0 ? `Total revenue: ฿${result.reduce((sum: number, r: any) => sum + (r.revenue || 0), 0).toLocaleString()}` : '',
        ].filter(Boolean),
      };
    }

    // Top/bottom items
    if ((intent === 'top' || intent === 'bottom') && Array.isArray(result)) {
      return {
        type: 'list',
        items: result.map((item: any) => ({
          name: item.name?.en || item.name || 'Unknown',
          revenue: item.revenue || 0,
          quantity: item.quantity || 0,
        })),
        insights: [`Showing ${intent === 'top' ? 'top' : 'bottom'} ${result.length} items by revenue`],
      };
    }

    // Summary data
    if (typeof result === 'object' && result !== null && 'totalRevenue' in result) {
      const r = result as { totalRevenue: number; totalOrders: number; avgOrderValue: number; totalDiscounts?: number };
      return {
        type: 'metric',
        value: r.totalRevenue,
        unit: 'THB',
        insights: [
          `Total orders: ${r.totalOrders?.toLocaleString() || 0}`,
          `Average order value: ฿${r.avgOrderValue?.toFixed(2) || 0}`,
          r.totalDiscounts ? `Total discounts: ฿${r.totalDiscounts?.toLocaleString()}` : '',
        ].filter(Boolean),
      };
    }
  }

  // Handle inventory data
  if (dataSource === 'inventory') {
    // Handle expiring items
    if (typeof result === 'object' && result !== null && 'expiringItems' in result) {
      const r = result as { expiringItems: any[]; count: number };
      return {
        type: 'list',
        items: r.expiringItems.map((item: any) => {
          const name = typeof item.name === 'object'
            ? (item.name?.en || item.name?.th || 'Unknown')
            : (item.name || 'Unknown');
          return {
            name,
            quantity: item.quantity,
            unit: item.unit,
            expiryDate: item.expiryDate,
            daysUntilExpiry: item.daysUntilExpiry,
            status: item.daysUntilExpiry <= 1 ? 'critical' : item.daysUntilExpiry <= 3 ? 'warning' : 'ok',
          };
        }),
        insights: [
          `${r.count} items expiring within 7 days`,
          r.expiringItems.filter((i: any) => i.daysUntilExpiry <= 1).length > 0
            ? `${r.expiringItems.filter((i: any) => i.daysUntilExpiry <= 1).length} items expire today or tomorrow!`
            : '',
        ].filter(Boolean),
      };
    }

    // Handle low stock items
    if (typeof result === 'object' && result !== null && 'lowStockItems' in result) {
      const r = result as { lowStockItems: any[]; count: number };
      return {
        type: 'list',
        items: r.lowStockItems.map((item: any) => {
          // Handle multilang name object
          const name = typeof item.name === 'object'
            ? (item.name?.en || item.name?.th || 'Unknown')
            : (item.name || 'Unknown');
          return {
            name,
            quantity: item.quantity,
            minStockLevel: item.minStockLevel,
            unit: item.unit,
            status: item.quantity <= 0 ? 'critical' : item.quantity <= item.minStockLevel ? 'low' : 'ok',
          };
        }),
        insights: [`${r.count} items need attention`],
      };
    }

    if (Array.isArray(result)) {
      return {
        type: 'list',
        items: result.slice(0, 20).map((item: any) => {
          // Handle both populated (ingredientId) and aggregated (ingredient) data
          const ingredientData = item.ingredientId || item.ingredient;
          const name = ingredientData?.name?.en || ingredientData?.name || item.name?.en || item.name || 'Unknown';
          return {
            name: typeof name === 'object' ? name.en || JSON.stringify(name) : name,
            quantity: item.quantity,
            unit: item.unit || ingredientData?.unit,
          };
        }),
        insights: [`Showing ${Math.min(result.length, 20)} inventory items`],
      };
    }
  }

  // Handle waste data
  if (dataSource === 'waste') {
    if (intent === 'trend' && Array.isArray(result)) {
      return {
        type: 'chart',
        chartData: result.map((item: any) => ({
          date: item._id,
          cost: item.totalCost || 0,
          count: item.count || 0,
        })),
        insights: [
          `Showing ${result.length} data points`,
          `Total waste cost: ฿${result.reduce((sum: number, r: any) => sum + (r.totalCost || 0), 0).toLocaleString()}`,
        ],
      };
    }

    if (typeof result === 'object' && result !== null && 'byCategory' in result) {
      const r = result as { byCategory: any[]; totalCost: number; totalCount: number };
      return {
        type: 'metric',
        value: r.totalCost,
        unit: 'THB',
        trend: 'down',
        items: r.byCategory?.map((cat: any) => ({
          name: cat._id || 'Unknown',
          count: cat.count,
          cost: cat.totalCost,
        })),
        insights: [
          `Total waste entries: ${r.totalCount}`,
          ...r.byCategory?.slice(0, 3).map((c: any) => `${c._id}: ฿${c.totalCost?.toLocaleString()}`) || [],
        ],
      };
    }
  }

  // Handle loyalty data
  if (dataSource === 'loyalty') {
    if (intent === 'trend' && Array.isArray(result)) {
      return {
        type: 'chart',
        chartData: result.map((item: any) => ({
          date: item._id,
          members: item.newMembers || 0,
        })),
        insights: [`New member signups over time`],
      };
    }

    if (typeof result === 'object' && result !== null && 'totalMembers' in result) {
      const r = result as { totalMembers: number; tierDistribution: any[]; recentSignups: number };
      return {
        type: 'metric',
        value: r.totalMembers,
        insights: [
          `Recent signups: ${r.recentSignups}`,
          ...(r.tierDistribution?.map((t: any) => `${t._id || 'Unknown'}: ${t.count} members`) || []),
        ],
      };
    }
  }

  // Handle promotions data
  if (dataSource === 'promotions' && Array.isArray(result)) {
    return {
      type: 'list',
      items: result.map((item: any) => ({
        name: item.promotion?.name?.en || item.promotion?.name || 'Unknown Promo',
        usageCount: item.usageCount,
        totalDiscount: item.totalDiscount,
      })),
      insights: [`${result.length} promotions with usage data`],
    };
  }

  // Handle transfers data
  if (dataSource === 'transfers') {
    if (typeof result === 'object' && result !== null && 'byStatus' in result) {
      const r = result as { byStatus: any[]; total: number };
      return {
        type: 'list',
        items: r.byStatus.map((item: any) => ({
          name: item._id || 'Unknown',
          count: item.count,
        })),
        insights: [`Total transfers: ${r.total}`],
      };
    }
  }

  // Handle purchasing data
  if (dataSource === 'purchasing') {
    if (typeof result === 'object' && result !== null && 'purchaseRequests' in result) {
      const r = result as { purchaseRequests: any[]; purchaseOrders: any[] };
      const prTotal = r.purchaseRequests?.reduce((sum: number, s: any) => sum + s.count, 0) || 0;
      const poTotal = r.purchaseOrders?.reduce((sum: number, s: any) => sum + s.count, 0) || 0;
      return {
        type: 'metric',
        value: prTotal + poTotal,
        insights: [
          `Purchase Requests: ${prTotal}`,
          ...r.purchaseRequests?.map((s: any) => `  - ${s._id}: ${s.count}`) || [],
          `Purchase Orders: ${poTotal}`,
          ...r.purchaseOrders?.slice(0, 3).map((s: any) => `  - ${s._id}: ${s.count}`) || [],
        ],
      };
    }
  }

  // Fallback for arrays
  if (Array.isArray(result)) {
    return {
      type: 'table',
      items: result.slice(0, 20),
      insights: [`Found ${result.length} records`],
    };
  }

  // Fallback for objects
  if (typeof result === 'object' && result !== null) {
    return {
      type: 'insight',
      insights: Object.entries(result).map(([key, value]) => `${key}: ${JSON.stringify(value)}`),
    };
  }

  return {
    type: 'insight',
    insights: ['Query executed successfully'],
  };
}

/**
 * Generate natural language answer from parsed query and result
 */
function generateAnswer(parsed: ParsedNLQQuery, responseData: NLQResponseData): string {
  const { intent, dataSource, timeRange } = parsed;
  const timeDesc = timeRange?.value === '0d' ? 'today' :
                   timeRange?.value === '1d' ? 'yesterday' :
                   timeRange?.value === '7d' ? 'last 7 days' :
                   timeRange?.value === '30d' ? 'last 30 days' :
                   timeRange?.value === 'this_week' ? 'this week' :
                   timeRange?.value === 'this_month' ? 'this month' :
                   'the selected period';

  const dataSourceLabels: Record<string, string> = {
    sales: 'sales',
    orders: 'orders',
    inventory: 'inventory',
    waste: 'waste',
    loyalty: 'loyalty members',
    promotions: 'promotions',
    transfers: 'transfers',
    purchasing: 'purchasing',
  };

  const source = dataSourceLabels[dataSource] || dataSource;

  if (intent === 'count') {
    return `Here's the count of ${source} for ${timeDesc}:`;
  }

  if (intent === 'trend') {
    return `Here's the ${source} trend for ${timeDesc}:`;
  }

  if (intent === 'top') {
    return `Here are the top performing items for ${timeDesc}:`;
  }

  if (intent === 'bottom') {
    return `Here are the lowest performing items for ${timeDesc}:`;
  }

  if (responseData.type === 'metric' && responseData.unit === 'THB') {
    return `Here's your ${source} summary for ${timeDesc}:`;
  }

  if (responseData.type === 'list') {
    return `Here are the ${source} items for ${timeDesc}:`;
  }

  return `Here's your ${source} data for ${timeDesc}:`;
}

const SUGGESTED_QUESTIONS = [
  'What were the total sales today?',
  'Top 10 selling items this week',
  'Show me low stock items',
  'Sales trend by day for last month',
  'How many loyalty members do we have?',
  'Waste cost this month',
];

export default function NLQChat() {
  const { t } = useTranslation();
  const componentId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState<string>(() => `nlq-${componentId}-${Date.now()}`);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const user = useAuthStore((state) => state.user);
  const isHQ = user?.role === 'HQ_ADMIN' || user?.role === 'HQ_PURCHASING';
  const nlqMutation = useNLQQuery();
  const { data: suggestionCategories } = useNLQSuggestions();

  // Clear conversation and reset session
  const handleClearConversation = () => {
    setMessages([]);
    setSessionId(`nlq-${componentId}-${Date.now()}`);
  };

  // Get flattened suggestions from backend or use defaults
  const suggestions = suggestionCategories?.flatMap(cat => cat.examples) || SUGGESTED_QUESTIONS;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSend = async () => {
    if (!input.trim() || nlqMutation.isPending) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const queryText = input.trim();
    setInput('');

    try {
      // HQ users query all branches by default (don't pass branchId)
      // Branch users will have their branchId set by the backend from auth
      const response = await nlqMutation.mutateAsync({
        query: queryText,
        branchId: isHQ ? undefined : undefined, // Let backend use user's branch for non-HQ
        sessionId, // Pass session for context-aware follow-ups
      });

      // Transform the backend response to UI format
      const transformedData = transformNLQResponse(
        response.result,
        response.parsed,
        response.metadata
      );

      // Add parsed info, metadata, and LLM-generated insights
      transformedData.parsed = response.parsed;
      transformedData.metadata = response.metadata;

      // Use LLM-generated insights if available, otherwise generate locally
      if (response.insights && response.insights.length > 0) {
        transformedData.insights = response.insights;
      }

      // Generate follow-up suggestions
      transformedData.suggestions = generateFollowUpSuggestions(response.parsed);

      // Use LLM-generated answer if available, otherwise generate locally
      const answer = response.answer || generateAnswer(response.parsed, transformedData);

      // Show confidence indicator if low
      const confidenceNote = response.parsed.confidence && response.parsed.confidence < 0.7
        ? ` (I'm ${Math.round(response.parsed.confidence * 100)}% confident about this interpretation)`
        : '';

      // Show clarification if needed
      const clarification = response.parsed.clarificationNeeded
        ? `\n\n${response.parsed.clarificationNeeded}`
        : '';

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: answer + confidenceNote + clarification,
        data: transformedData,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error: any) {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: error?.response?.data?.message || t('analytics.nlqError') || 'Sorry, I could not process that query. Please try rephrasing.',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInput(suggestion);
    inputRef.current?.focus();
  };

  const generateFollowUpSuggestions = (parsed: ParsedNLQQuery): string[] => {
    const { dataSource, intent } = parsed;
    const suggestions: string[] = [];

    if (dataSource === 'sales' || dataSource === 'orders') {
      if (intent !== 'trend') suggestions.push('Show sales trend by day');
      if (intent !== 'top') suggestions.push('Top 5 selling items');
      suggestions.push('Compare to last week');
    } else if (dataSource === 'inventory') {
      suggestions.push('Show expiring items');
      suggestions.push('List all low stock');
    } else if (dataSource === 'waste') {
      suggestions.push('Waste trend by day');
      suggestions.push('Top waste categories');
    } else if (dataSource === 'loyalty') {
      suggestions.push('New members this month');
      suggestions.push('Members by tier');
    }

    return suggestions.slice(0, 3);
  };

  const renderDataVisualization = (data: NLQResponseData, messageContent: string) => {
    if (!data) return null;

    // Extract metrics from data for highlighting
    const metrics: { label: string; value: number | string; change?: number; direction?: 'up' | 'down' | 'stable'; prefix?: string }[] = [];

    // Check for comparison data and extract metrics
    if (data.chartData && typeof data.chartData === 'object' && 'current' in data.chartData && 'changes' in data.chartData) {
      const comparison = data.chartData as {
        current: { data: Record<string, number> };
        previous: { data: Record<string, number> };
        changes: Record<string, { change: number; direction: 'up' | 'down' | 'stable' }>;
      };
      const currentData = comparison.current?.data || {};
      const changes = comparison.changes || {};

      if (currentData.totalRevenue !== undefined) {
        metrics.push({
          label: 'Revenue',
          value: currentData.totalRevenue,
          change: changes.totalRevenue?.change,
          direction: changes.totalRevenue?.direction,
          prefix: '฿',
        });
      }
      if (currentData.totalOrders !== undefined) {
        metrics.push({
          label: 'Orders',
          value: currentData.totalOrders,
          change: changes.totalOrders?.change,
          direction: changes.totalOrders?.direction,
        });
      }
      if (currentData.avgOrderValue !== undefined) {
        metrics.push({
          label: 'Avg Order',
          value: Math.round(currentData.avgOrderValue),
          change: changes.avgOrderValue?.change,
          direction: changes.avgOrderValue?.direction,
          prefix: '฿',
        });
      }
    } else if (data.type === 'metric' && data.value !== undefined) {
      metrics.push({
        label: data.unit === 'THB' ? 'Total Revenue' : 'Total',
        value: data.value,
        prefix: data.unit === 'THB' ? '฿' : undefined,
      });
    }

    // Prepare chart data for visualization
    let chartData: any = null;
    if (data.type === 'chart' && data.chartData) {
      chartData = data.chartData;
    } else if (data.type === 'list' && data.items) {
      chartData = data.items;
    } else if (data.type === 'table' && data.items) {
      chartData = data.items;
    }

    // Format insights with types based on content
    const formattedInsights = data.insights?.map((insight) => {
      const lower = insight.toLowerCase();
      if (lower.includes('decrease') || lower.includes('decline') || lower.includes('lower') || lower.includes('drop')) {
        return { text: insight, type: 'warning' as const };
      }
      if (lower.includes('increase') || lower.includes('higher') || lower.includes('grew') || lower.includes('up')) {
        return { text: insight, type: 'trend' as const };
      }
      if (lower.includes('peak') || lower.includes('highest') || lower.includes('best') || lower.includes('top')) {
        return { text: insight, type: 'highlight' as const };
      }
      return { text: insight, type: 'info' as const };
    });

    // Render using AnalyticsResponse
    return (
      <div className="mt-3">
        <AnalyticsResponse
          summary={messageContent}
          metrics={metrics}
          insights={formattedInsights}
          visualization={chartData ? (
            <SmartChart
              data={chartData}
              parsed={data.parsed}
              expanded={isExpanded}
            />
          ) : undefined}
          metadata={data.metadata}
          suggestions={data.suggestions}
          onSuggestionClick={handleSuggestionClick}
          expanded={isExpanded}
        />
      </div>
    );
  };

  const panelClasses = isExpanded
    ? 'fixed inset-4 md:inset-8 lg:inset-16'
    : 'fixed bottom-24 right-6 w-96 h-[600px]';

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 p-4 rounded-full shadow-lg transition-all z-50 ${
          isOpen ? 'bg-destructive text-destructive-foreground' : 'bg-primary text-primary-foreground'
        }`}
      >
        {isOpen ? <X className="h-6 w-6" /> : <MessageSquare className="h-6 w-6" />}
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div className={`${panelClasses} bg-card border rounded-lg shadow-xl flex flex-col z-50 transition-all duration-200`}>
          {/* Header */}
          <div className="p-4 border-b flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-primary/20">
                <Bot className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">{t('analytics.nlqTitle')}</h3>
                <p className="text-xs text-muted-foreground">{t('analytics.nlqSubtitle')}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <button
                  onClick={handleClearConversation}
                  className="p-2 rounded-lg hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
                  title="Clear conversation"
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-2 rounded-lg hover:bg-accent transition-colors"
                title={isExpanded ? 'Minimize' : 'Expand'}
              >
                {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 ? (
              <div className="text-center py-8">
                <Sparkles className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-sm text-muted-foreground mb-4">
                  {t('analytics.nlqWelcome')}
                </p>
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">{t('analytics.trySaying')}:</p>
                  {suggestions.slice(0, isExpanded ? 8 : 4).map((q, i) => (
                    <button
                      key={i}
                      onClick={() => handleSuggestionClick(q)}
                      className="block w-full text-left text-sm p-2 rounded bg-secondary/50 hover:bg-secondary transition-colors"
                    >
                      "{q}"
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
                >
                  <div className={`p-2 rounded-full h-8 w-8 flex items-center justify-center shrink-0 ${
                    message.role === 'user' ? 'bg-primary' : 'bg-secondary'
                  }`}>
                    {message.role === 'user' ? (
                      <User className="h-4 w-4 text-primary-foreground" />
                    ) : (
                      <Bot className="h-4 w-4" />
                    )}
                  </div>
                  <div className={`flex-1 ${message.role === 'user' ? 'text-right' : ''} ${
                    message.role === 'user' && !isExpanded ? 'max-w-[280px]' : 'max-w-none'
                  }`}>
                    {/* Only show text bubble for user messages or assistant messages without data */}
                    {(message.role === 'user' || !message.data) && (
                      <div className={`inline-block p-3 rounded-lg ${
                        message.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-secondary'
                      }`}>
                        <p className="text-sm">{message.content}</p>
                      </div>
                    )}
                    {/* For assistant messages with data, use the polished AnalyticsResponse component */}
                    {message.role === 'assistant' && message.data && renderDataVisualization(message.data, message.content)}
                  </div>
                </div>
              ))
            )}
            {nlqMutation.isPending && (
              <div className="flex gap-3">
                <div className="p-2 rounded-full h-8 w-8 flex items-center justify-center bg-secondary">
                  <Bot className="h-4 w-4" />
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('analytics.analyzing')}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t shrink-0">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                placeholder={t('analytics.askAnything')}
                className="flex-1 rounded-lg border bg-background px-3 py-2 text-sm"
                disabled={nlqMutation.isPending}
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || nlqMutation.isPending}
                className="p-2 rounded-lg bg-primary text-primary-foreground disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
